import React, { useCallback, useEffect, useMemo, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import { Divider, Form } from "antd";
import NGIInput from "components/Common/inputs/NGIInput";
import { Option } from "antd/es/mentions";
import NGISelect from "components/Common/inputs/NGISelect";
import { useSelector } from "react-redux";
import { DictionariesSelector } from "store/dictionaries/selectors";
import { USER_CURRENCY } from "utils/constants";
import NGIDatePicker from "components/Common/inputs/NGIDatePicker";
import {
  PRICE_TYPES_OPTION,
  PRICING_OPTION_MARK,
  PRICING_OPTIONS,
  TITLE_MAP,
} from "pages/price-management/Prices/variables";
import classnames from "classnames";
import { getEndOfTheDay, getStartOfTheDay } from "components/Common/formatter";
import { getPrices } from "utils/api";
import moment from "moment/moment";

const useStyles = makeStyles({
  formContainer: {
    display: "flex",
    flexDirection: "column",
  },
  formItem: {
    flex: "1!important",
    margin: "0px!important",
  },
  formRow: {
    display: "flex",
    padding: "10px 0",
    gap: 15,
  },
  tabContent: {
    marginTop: 15,
  },
  rowTitle: {
    flex: 1,
  },
  title: {
    fontSize: 14,
    fontWeight: 600,
  },
  subTitle: {
    fontSize: 11,
    fontWeight: 300,
    color: "#000000BF",
  },
  liteSelect: {
    "&.ant-select:not(.ant-select-customize-input) .ant-select-selector": {
      border: "2px solid #0000001A",
      borderRadius: 5,
    },
  },
  liteInput: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  liteDate: {
    border: "2px solid #0000001A",
    borderRadius: 5,
  },
  currency: {
    backgroundColor: "#0000000D",
    borderRadius: 2,
    fontSize: 9,
    color: "#666666",
    padding: "2px 8px",
  },
  optionValue: {
    "&>div": {
      color: "#8C8C8C",
      fontWeight: 300,
      fontSize: 9,
    },
  },
  selectedValue: {
    "& .ant-select-selection-item": {
      height: 30,
    },
  },
  disabledField: {
    flex: 1,
  },
  calculatedInput: {
    backgroundColor: "#0085FF0F!important",

    "&> input": {
      backgroundColor: "transparent!important",
    },
  },
  basePriceContainer: {
    display: "flex",
    justifyContent: "space-between",
    backgroundColor: "#0000000A",
    padding: 15,
    fontWeight: 300,
    color: "#000000BF",
    marginBottom: 10,
  },
  amountContainer: {
    display: "flex",
    alignItems: "center",
    color: "#000000BF",
    fontWeight: 700,

    "&>span:first-child": {
      marginRight: 10,
    },
  },
});

const ProductPrice = ({ type, priceData, consumptionBandList, customerGroupList, form, validateForm, status }) => {
  const classes = useStyles();
  const [selectedPricingOption, setSelectedPricingOption] = useState(priceData?.pricingOption || null);
  const [basePrice, setBasePrice] = useState(0);
  const [amount, setAmount] = useState(priceData?.amount || 0);

  const {
    list: { productTypes },
  } = useSelector(DictionariesSelector);

  const calculatedFinalPrice = useMemo(() => {
    switch (selectedPricingOption) {
      case "Actual":
        return amount;
      case "DiscountPercentage":
        return ((100 - amount) / 100) * basePrice;
      case "DiscountAmount":
        return basePrice - amount;
      default:
        return 0;
    }
  }, [selectedPricingOption, form, basePrice, amount]);

  const selectedBand = useMemo(() => {
    if (type === PRICE_TYPES_OPTION.CONSUMPTION_BAND) {
      return consumptionBandList.find(item => item.id === priceData?.consumptionBand);
    }
    return null;
  }, []);

  const selectedCustomerGroup = useMemo(() => {
    if (type === PRICE_TYPES_OPTION.CUSTOMER_GROUP) {
      return customerGroupList.find(item => item.id === priceData?.customerGroup);
    }
    return null;
  }, []);

  const calculateAndValidate = useCallback(() => {
    if (
      type === PRICE_TYPES_OPTION.DEPOT ||
      type === PRICE_TYPES_OPTION.CONSUMPTION_BAND ||
      type === PRICE_TYPES_OPTION.CUSTOMER_GROUP
    ) {
      const { product, activationDate } = form.getFieldsValue();
      if (product && activationDate) {
        getPrices({
          type: "Default",
          product,
          maxActivationAt: getStartOfTheDay(activationDate),
          registrationCategory: priceData.registrationCategory,
          minDeactivationAt: getEndOfTheDay(activationDate),
        }).then(({ data }) => {
          if (data?.result?.items?.length) {
            const { amount } = data?.result?.items[0];
            setBasePrice(amount);
            return;
          }
          setBasePrice(0);
        });
      } else {
        setBasePrice(0);
      }
    }
  }, [type, form, priceData]);

  const onPriceOptionChange = useCallback(value => setSelectedPricingOption(value), []);
  const onAmountChange = useCallback(e => setAmount(e.target.value), []);

  useEffect(() => {
    if (type === PRICE_TYPES_OPTION.CUSTOMER_GROUP) {
      form.setFieldValue("pricingOption", "Actual");
      setSelectedPricingOption("Actual");
    }
  }, []);

  return (
    <Form onChange={validateForm} form={form} name="productPrice" layout="inline" className={classes.formContainer}>
      <div className={classes.tabContent}>
        <div className={classes.formRow}>
          <div className={classes.rowTitle}>
            <div className={classes.title}>Select Product</div>
            <div className={classes.subTitle}>Please select one of the products to set a new price</div>
          </div>
          <Form.Item
            className={classes.formItem}
            name="product"
            rules={[{ required: true, message: "Please select value" }]}
          >
            <NGISelect
              onChange={calculateAndValidate}
              className={classes.liteSelect}
              showSearch
              disabled={status === "Active"}
              placeholder="Select value"
            >
              {productTypes?.map(item => (
                <Option key={item} value={item}>
                  {item}
                </Option>
              ))}
            </NGISelect>
          </Form.Item>
        </div>

        <Divider />

        {(type === PRICE_TYPES_OPTION.DEPOT ||
          type === PRICE_TYPES_OPTION.CONSUMPTION_BAND ||
          type === PRICE_TYPES_OPTION.CUSTOMER_GROUP) && (
          <>
            {!!basePrice && (
              <div className={classes.basePriceContainer}>
                <span>Base Price for the selected product:</span>
                <span className={classes.amountContainer}>
                  <span>{basePrice}</span>
                  <span className={classes.currency}>{USER_CURRENCY()}</span>
                </span>
              </div>
            )}

            <div className={classes.formRow}>
              <div className={classes.rowTitle}>
                <div className={classes.title}>Change to</div>
                <div className={classes.subTitle}>Select how you want to update the price</div>
              </div>
              <Form.Item
                className={classes.formItem}
                name="pricingOption"
                rules={[{ required: true, message: "Please select value" }]}
              >
                <NGISelect
                  className={classnames(classes.liteSelect, classes.selectedValue)}
                  showSearch
                  disabled={type === PRICE_TYPES_OPTION.CUSTOMER_GROUP || status === "Active"}
                  onChange={onPriceOptionChange}
                  placeholder="Select value"
                >
                  {PRICING_OPTIONS?.map(item => (
                    <Option key={item.value} value={item.value}>
                      <div className={classes.optionValue}>
                        {item.title}
                        <div>{item.subTitle}</div>
                      </div>
                    </Option>
                  ))}
                </NGISelect>
              </Form.Item>
            </div>
          </>
        )}

        <div className={classes.formRow}>
          <div className={classes.rowTitle}>
            <div className={classes.title}>{TITLE_MAP[type].amount.title}</div>
            <div className={classes.subTitle}>{TITLE_MAP[type].amount.subTitle}</div>
          </div>
          <Form.Item
            className={classes.formItem}
            name="amount"
            rules={[
              { required: true, message: "Please enter value" },
              {
                message: "Should be less or equal to 100%",
                validator: (_, value) => {
                  if (value > 100 && selectedPricingOption === "DiscountPercentage") {
                    return Promise.reject("Should be less or equal to 100%");
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <NGIInput
              type="number"
              placeholder="Product Price"
              className={classes.liteInput}
              onChange={onAmountChange}
              disabled={status === "Active"}
              suffix={
                <span className={classes.currency}>
                  {PRICING_OPTION_MARK[selectedPricingOption] || USER_CURRENCY()}
                </span>
              }
            />
          </Form.Item>
        </div>

        {(type === PRICE_TYPES_OPTION.DEPOT ||
          type === PRICE_TYPES_OPTION.CONSUMPTION_BAND ||
          type === PRICE_TYPES_OPTION.CUSTOMER_GROUP) && (
          <div className={classes.formRow}>
            <div className={classes.rowTitle}>
              <div className={classes.title}>Update Final Price</div>
              <div className={classes.subTitle}>The price applicable for the customers</div>
            </div>
            <div className={classes.disabledField}>
              <NGIInput
                disabled
                placeholder="0"
                value={calculatedFinalPrice}
                className={classnames(classes.liteInput, classes.calculatedInput)}
                suffix={<span className={classes.currency}>{USER_CURRENCY()}</span>}
              />
            </div>
          </div>
        )}

        <Divider />

        <div className={classes.formRow}>
          <div className={classes.rowTitle}>
            <div className={classes.title}>Price Start Date</div>
            <div className={classes.subTitle}>Set the first day this price will be active and applied to customers</div>
          </div>
          <Form.Item
            className={classes.formItem}
            name="activationDate"
            rules={[{ required: true, message: "Please select date" }]}
          >
            <NGIDatePicker
              onChange={() => {
                calculateAndValidate();
                form.setFieldValue("deactivationDate", null);
              }}
              disabled={status === "Active"}
              disableDates={current => {
                if (type === PRICE_TYPES_OPTION.CONSUMPTION_BAND && selectedBand?.status === "Draft") {
                  return (
                    moment(selectedBand?.activationAt)?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) >=
                      current ||
                    moment(selectedBand?.deactivationAt)?.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }) <=
                      current
                  );
                }
                if (type === PRICE_TYPES_OPTION.CUSTOMER_GROUP && selectedCustomerGroup?.status === "Draft") {
                  return (
                    moment(selectedCustomerGroup?.activationAt)?.set({
                      hour: 0,
                      minute: 0,
                      second: 0,
                      millisecond: 0,
                    }) >= current ||
                    (selectedCustomerGroup?.deactivationAt
                      ? moment(selectedCustomerGroup?.deactivationAt)?.set({
                          hour: 12,
                          minute: 0,
                          second: 0,
                          millisecond: 0,
                        }) <= current
                      : false)
                  );
                }
                return moment() >= current;
              }}
              className={classes.liteInput}
              placeholder="Price Start Date"
            />
          </Form.Item>
        </div>

        <div className={classes.formRow}>
          <div className={classes.rowTitle}>
            <div className={classes.title}>Price End Date</div>
            <div className={classes.subTitle}>
              Set the last day this price will be active. Leave blank for unlimited time.
            </div>
          </div>
          <Form.Item className={classes.formItem} name="deactivationDate">
            <NGIDatePicker
              allowClear
              disableDates={current => {
                if (type === PRICE_TYPES_OPTION.CONSUMPTION_BAND && selectedBand?.status === "Draft") {
                  return (
                    form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) >
                      current ||
                    moment(selectedBand?.deactivationAt)?.set({ hour: 12, minute: 0, second: 0, millisecond: 0 }) <=
                      current
                  );
                }
                if (type === PRICE_TYPES_OPTION.CUSTOMER_GROUP && selectedCustomerGroup?.status === "Draft") {
                  return (
                    form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) >
                      current ||
                    (selectedCustomerGroup?.deactivationAt
                      ? moment(selectedCustomerGroup?.deactivationAt)?.set({
                          hour: 12,
                          minute: 0,
                          second: 0,
                          millisecond: 0,
                        }) <= current
                      : false)
                  );
                }
                return (
                  form.getFieldValue("activationDate")?.set({ hour: 0, minute: 0, second: 0, millisecond: 0 }) > current
                );
              }}
              className={classes.liteInput}
              placeholder="Price End Date"
            />
          </Form.Item>
        </div>
      </div>
    </Form>
  );
};

export default ProductPrice;
